import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { fontFamilyBody, hover } from '../styles/helpers/mixins';
import { SUCCESS, FAILURE, LOADING, INITIAL } from '../constants/status';
import { axiosSingleton } from '../api/generalApi';

class NewsletterSignUpForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      consent: false
    };

    this.timeoutLength = 8000;

    this.inputHandlerEmail = this.inputHandlerEmail.bind(this);
    this.inputHandlerConsent = this.inputHandlerConsent.bind(this);
    this.formHandler = this.formHandler.bind(this);
  }

  inputHandlerEmail(event) {
    this.setState({
      email: event.target.value
    });
  }

  inputHandlerConsent(event) {
    this.setState({
      consent: event.target.checked
    });
  }

  formHandler(event) {
    const { onUpdateStatus, onClose } = this.props;
    const { email, consent } = this.state;
    event.preventDefault();

    if (email && consent) {
      onUpdateStatus(LOADING);

      axiosSingleton
        .post('/newsletter', { email, source: 'modal' })
        .then(() => {
          onUpdateStatus(SUCCESS);

          setTimeout(() => {
            onClose();
          }, this.timeoutLength);
        })
        .catch(error => {
          onUpdateStatus(FAILURE);
          setTimeout(() => {
            onUpdateStatus(INITIAL);
            this.setState({
              email: '',
              consent: false
            });
          }, this.timeoutLength);
          throw error;
        });
    }
  }

  render() {
    const { email, consent } = this.state;
    const { buttonText, placeholder, privacyPolicy } = this.props;

    // if any required data is missing, don't render the widget
    if (!privacyPolicy) return null;

    if (buttonText) {
      return (
        <Form onSubmit={this.formHandler}>
          <EmailInput
            name="email"
            type="email"
            autoComplete="off"
            required
            placeholder={placeholder}
            onChange={this.inputHandlerEmail}
          />

          <SubmitButton disabled={!email || !consent}>{buttonText}</SubmitButton>

          <Checkbox lightTheme>
            <span dangerouslySetInnerHTML={{ __html: privacyPolicy }} />
            <input type="checkbox" name="consent" required onChange={this.inputHandlerConsent} />
            <span className="checkmark" />
          </Checkbox>
        </Form>
      );
    }

    return (
      <Form onSubmit={this.formHandler}>
        <InputContainer>
          <Input
            name="email"
            type="email"
            autoComplete="off"
            required
            placeholder={placeholder}
            onChange={this.inputHandlerEmail}
          />
          <FormSubmit disabled={!email || !consent}>
            <SubmitIcon src="/images/icons/arrow-white.svg" />
          </FormSubmit>
        </InputContainer>

        <InputContainer>
          <Checkbox>
            <span dangerouslySetInnerHTML={{ __html: privacyPolicy }} />
            <input type="checkbox" name="consent" required onChange={this.inputHandlerConsent} />
            <span className="checkmark" />
          </Checkbox>
        </InputContainer>
      </Form>
    );
  }
}

NewsletterSignUpForm.defaultProps = {
  onClose: () => {},
  onUpdateStatus: () => {},
  placeholder: '',
  privacyPolicy: undefined,
  buttonText: undefined
};

NewsletterSignUpForm.propTypes = {
  onClose: PropTypes.func,
  onUpdateStatus: PropTypes.func,
  placeholder: PropTypes.string,
  privacyPolicy: PropTypes.string,
  buttonText: PropTypes.string
};

export default NewsletterSignUpForm;

/*
  STYLES
*/
const Form = styled.form`
  width: 100%;
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
`;

const Input = styled.input`
  ${fontFamilyBody};
  width: 100%;
  height: 40px;
  background-color: #343434;
  border: solid 1px #343434;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: italic;
  line-height: 1.57;
  letter-spacing: normal;
  color: #fff;
  padding-left: 14px;
  border-radius: 0;
  box-shadow: none;
  -webkit-appearance: none;

  &:focus {
    outline: 0;
    border-color: #9e9e9e;
  }

  &::placeholder {
    color: #b8b8b8;
  }

  @media screen and (min-width: 680px) {
    font-size: 14px;
  }
`;

const Checkbox = styled.label`
  display: block;
  position: relative;
  padding-left: 24px;
  cursor: pointer;
  user-select: none;
  margin: 14px 0 0;

  p {
    margin: 0;
    ${fontFamilyBody};
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: italic;
    line-height: 1.25;
    letter-spacing: normal;
    color: ${props => (props.lightTheme ? '#6a6a6a' : '#9e9e9e')};

    a {
      color: ${props => (props.lightTheme ? '#6a6a6a' : '#9e9e9e')};

      &:hover {
        color: ${props => (props.lightTheme ? '#000' : '#fff')};
      }
    }
  }

  /* Hide the browser's default checkbox */
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  /* Create a custom checkbox */
  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    width: 16px;
    border: solid 1px ${props => (props.lightTheme ? '#9e9e9e' : '#9e9e9e')};
    background-color: ${props => (props.lightTheme ? '#f2f2f2' : '#1f1f1f')};

    /* Create the checkmark/indicator (hidden when not checked) */
    &:after {
      content: '';
      position: absolute;
      display: none;
      left: 3px;
      top: 3px;
      width: 8px;
      height: 8px;
      background-color: ${props => (props.lightTheme ? '#779182' : '#fff')};
    }
  }

  /* Show the checkmark when checked */
  input:checked ~ .checkmark:after {
    display: block;
  }
`;

const FormSubmit = styled.button`
  min-width: 40px;
  height: 40px;
  border: solid 1px #ffffff;
  background-color: #343434;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;

  &:focus {
    outline: 0;
  }

  &:disabled {
    cursor: default;
    opacity: 0.25;
  }
`;

const SubmitIcon = styled.img`
  height: 12px;
  width: 12px;
  transform: rotateZ(-90deg);
`;

const EmailInput = styled.input`
  border: solid 1px #c5c5c5;
  background-color: #f2f2f2;
  font-size: 16px;
  padding: 15px;
  width: 100%;
  margin: 0 0 10px;
`;

const SubmitButton = styled.button`
  ${fontFamilyBody};
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 1.5px;
  text-align: center;
  color: #ffffff;
  text-transform: uppercase;
  width: 100%;
  background-color: #779182;
  border: none;
  padding: 17px;
  margin: 0;
  transition: opacity 0.25s ease;
  cursor: pointer;

  ${hover`
    opacity: .8;
  `};

  &:disabled {
    opacity: 1;
    cursor: default;
  }
`;



// WEBPACK FOOTER //
// ./src/components/NewsletterSignUpForm.js